<template>
  <component
    v-bind="attributes"
    :is="computedElement"
    :class="b(modifiers)"
    :style="style"
    :disabled="!!$attrs.disabled ? true : null"
    @touchstart="hasTouch = true"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @focus="onFocus"
    @blur="onBlur"
    @click="onClick"
  >
    <span :class="b('inner')">
      <!-- @slot Button content. -->
      <slot v-if="!showIcon || iconPosition === 'right'"></slot>
      <e-icon-sprite
        v-if="showIcon"
        :class="b('icon', { position: iconPosition })"
        :icon="icon"
        :height="iconSize"
        :width="iconSize"
      />
      <!-- @slot Button content. -->
      <slot v-if="showIcon && iconPosition === 'left'"></slot>
    </span>
    <e-progress v-if="progress" />
  </component>
</template>

<script>
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eProgress from '@/elements/e-progress.vue';
  import propScale from '@/helpers/prop.scale.js';

  /**
   * Renders a `<button>` or `<a>` element (based on existing `href` attribute) with button style.
   * The component uses a `<slot>` to render the content.
   *
   * [You can also define inherited attributes for `<button>`](https://developer.mozilla.org/de/docs/Web/HTML/Element/button#Attribute)
   *
   * [You can also define inherited attributes for `<a>`](https://developer.mozilla.org/de/docs/Web/HTML/Element/a#Attribute)
   */
  export default {
    name: 'e-button',
    components: {
      eProgress,
      eIconSprite,
    },

    // mixins: [],

    props: {
      /**
       * Sets the button height.
       */
      size: {
        type: String,
        default: 'medium',
        validator(value) {
          return ['small', 'medium', 'large'].includes(value);
        },
      },

      /**
       * Modifies the inner spacing for the button.
       *
       * Valid values: `[0, 100, 500, 600, 700]`
       */
      spacing: propScale(500, [0, 100, 200, 500, 600, 700]),

      /**
       * If `true` the button gets the negative style
       *
       * @deprecated Before 'variant' was introduced, this effectively did what now is variant 'primary'. From now on this will only switch variant 'default' to 'primary' instead of applying '--negative' class.
       */
      negative: {
        type: Boolean,
        default: false,
      },

      /**
       * If `true` the button shows a progress animation
       * TODO: Add progress animation (default project loader can't be used here)
       */
      progress: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to use a different button style.
       */
      variant: {
        type: String,
        default: 'default',
        validator(value) {
          return [
            'default', // NOTE: named 'secondary' in new designs
            'primary',
            'primary-quiet',
            'primary-text',
            'secondary', // NOTE: named 'highlighted' in new designs
            'secondary-quiet',
            'secondary-text',
          ].includes(value);
        },
      },

      /**
       * Forces the hover state
       */
      hover: {
        type: Boolean,
        default: false,
      },

      /**
       * Forces the focus state
       */
      focus: {
        type: Boolean,
        default: false,
      },

      /**
       * Forces the active state
       */
      active: {
        type: Boolean,
        default: false,
      },

      /**
       * Disables the button
       */
      disabled: {
        type: Boolean,
        default: false,
      },

      /**
       * Icon to show within the button
       */
      icon: {
        type: String,
        default: null,
      },

      /**
       * Define the placement of the icon.
       */
      iconPosition: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'right'].includes(value);
        },
      },

      /**
       * Allows to set a custom root element.
       */
      element: {
        type: String,
        default: null,
      },

      /**
       * Allows to define the width of the button.
       */
      width: {
        type: String,
        default: 'full',
        validator(value) {
          return ['full', 'auto'].includes(value);
        },
      },
    },

    emits: ['click'],

    data() {
      return {
        /**
         * @type {Boolean} Internal flag to determine hover state.
         */
        hasHover: this.hover,

        /**
         * @type {Boolean} Internal flag to determine active state.
         */
        isActive: this.active,

        /**
         * @type {Boolean} Internal flag to determine focus state.
         */
        hasFocus: this.focus,

        /**
         * Determines if the current device uses touch.
         */
        hasTouch: false,
      };
    },

    computed: {
      /**
       * Determines if the icon should be rendered.
       *
       * @returns {Boolean}
       */
      showIcon() {
        return !!this.icon && !this.variant.endsWith('-text');
      },

      /**
       * Returns the custom or scenario related root element.
       *
       * @returns {String}
       */
      computedElement() {
        return this.element || (this.$attrs.href ? 'a' : 'button');
      },

      /**
       * Returns an Object of class modifiers.
       *
       * @returns {Object}
       */
      modifiers() {
        const variant = this.variant === 'default' && this.negative ? 'primary' : this.variant;

        return {
          spacing: this.spacing,
          progress: this.progress,
          disabled: this.disabled,
          variant,
          size: this.size,
          hover: this.hover || this.hasHover,
          focus: this.focus || this.hasFocus,
          active: this.active || this.isActive,
          touch: this.hasTouch,
          width: this.width,
          negative: this.negative,
        };
      },

      /**
       * Returns an Object of attributes.
       *
       * @returns {Object}
       */
      attributes() {
        return {
          role: this.$attrs.href ? 'button' : null, // Fallback
          ...this.$attrs,
          disabled: this.disabled || this.progress,
        };
      },

      /**
       * Returns inline styles to keep dimensions during progress state.
       *
       * @returns {Object}
       */
      style() {
        return this.progress && this.width !== 'full' ? this.getElementDimensions() : null;
      },

      /**
       * Computes the icon size depending on the viewport.
       *
       * @returns {Object}
       */
      iconSize() {
        if (this.$viewport.isMd) {
          return 16;
        }

        if (this.$viewport.isSm) {
          return 24;
        }

        return 32;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Mouseenter event handler.
       */
      onMouseEnter() {
        this.hasHover = true;
      },

      /**
       * Mouseleave event handler.
       */
      onMouseLeave() {
        this.hasHover = false;
        this.isActive = false;
      },

      /**
       * Mousedown event handler.
       */
      onMouseDown() {
        this.isActive = true;
      },

      /**
       * Mouseup event handler.
       */
      onMouseUp() {
        this.isActive = false;
      },

      /**
       * Focus event handler.
       */
      onFocus() {
        this.hasFocus = true;
      },

      /**
       * Blur event handler.
       */
      onBlur() {
        this.hasHover = false;
        this.hasFocus = false;
      },

      /**
       * Click event handler.
       *
       * @param {Event} event - The click event instance.
       */
      onClick(event) {
        this.$el.blur();

        /**
         * Click event
         *
         * @event click
         *
         * @property {Event} event - The original DOM event.
         */
        this.$emit('click', event);
      },

      /**
       * Returns the current width and height of the button.
       *
       * @returns {Object}
       */
      getElementDimensions() {
        const element = this.$el;

        return element ? { width: `${element.offsetWidth}px`, height: `${element.offsetHeight}px` } : null;
      },
    },
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  // @use '@/setup/scss/extends' as *;

  .e-button {
    $this: &;

    position: relative;
    display: block;
    width: 100%;
    padding: functions.get-theme-var('e-button-padding');
    border: functions.get-theme-var('e-button-border-width') solid functions.get-theme-var('e-button-border-color');
    border-radius: functions.rem(functions.get-theme-var('e-button-border-radius'));
    background: functions.get-theme-var('e-button-background-color');
    cursor: pointer;
    color: functions.get-theme-var('e-button-text-color');
    font-size: functions.get-theme-var('e-button-font-size');
    font-weight: functions.get-theme-var('e-button-font-weight');
    text-align: center;

    @include mixins.media(lg) {
      font-size: functions.get-theme-var('e-button-font-size--lg');
    }

    &--size-small {
      min-height: functions.get-theme-var('e-button-height--small');
      line-height: functions.get-theme-var('e-button-line-height--small');
    }

    &--size-medium {
      min-height: functions.get-theme-var('e-button-height--medium');
      line-height: functions.get-theme-var('e-button-line-height--medium');
    }

    &--size-large {
      min-height: functions.get-theme-var('e-button-height--large');
      line-height: functions.get-theme-var('e-button-line-height--large');
    }

    &:hover {
      text-decoration: none;
    }

    &__inner {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1em;
      vertical-align: baseline;
    }

    &__icon {
      &--position-left {
        margin-right: variables.$spacing--10;
      }

      &--position-right {
        margin-left: variables.$spacing--10;
      }
    }

    .e-progress {
      display: none;
      width: 1em;
      height: 1em;
    }

    &:focus,
    &--focus,
    &:hover:not(&--touch),
    &--hover:not(&--touch) {
      outline: none;
      border-color: functions.get-theme-var('e-button-border-color--state');
      color: functions.get-theme-var('e-button-text-color--state');
    }

    &:active:not([disabled]),
    &--active:not([disabled]) {
      position: relative;
      border-color: functions.get-theme-var('e-button-border-color--state');
    }

    &--focus path,
    &--hover:not(&--touch) path,
    &:focus path,
    &:hover:not(&--touch) path {
      fill: functions.get-theme-var('e-button-svg-fill--state');
    }

    &[disabled],
    &--disabled,
    &[disabled]:hover,
    &--disabled:hover {
      border-color: functions.get-theme-var('e-button-border-color--disabled');
      background: functions.get-theme-var('e-button-background-color');
      cursor: default;
      color: functions.get-theme-var('e-button-text-color--disabled');
      pointer-events: none;
    }

    &--spacing-0 {
      min-height: unset;
      padding: functions.rem(variables.$spacing--2 0 0); // Improves vertical centering.
    }

    &--spacing-100 {
      min-height: unset;
      padding: functions.rem(variables.$spacing--2 variables.$spacing--10); // Improves vertical centering.
    }

    &--spacing-200 {
      min-height: unset;
      padding: functions.rem(8px variables.$spacing--20); // Improves vertical centering.
    }

    &--spacing-600 {
      min-height: unset;
      padding-right: functions.get-theme-var('e-button-padding-right--spacing-600');
      padding-left: functions.get-theme-var('e-button-padding-left--spacing-600');
    }

    &--spacing-700 {
      min-height: unset;
      padding-right: functions.rem(variables.$spacing--50);
      padding-left: functions.rem(variables.$spacing--50);
    }

    &--progress {
      #{$this}__inner {
        // This style keeps the width of the element in progress state.
        display: block;
        visibility: hidden;
        height: 0;
      }

      .e-progress {
        display: inline-block;
        margin-top: functions.rem(1px);
        margin-bottom: functions.rem(1px);
      }
    }

    &--width-auto {
      display: inline-block;
      width: auto;
    }
  }

  .e-button--variant-primary {
    &:not([disabled]) {
      background-color: functions.get-theme-var('e-button-primary-background-color');
      color: functions.get-theme-var('e-button-primary-text-color');

      &.e-button:focus:not(.e-button--progress),
      &.e-button--focus:not(.e-button--progress),
      &.e-button:hover:not(.e-button--touch):not(.e-button--progress),
      &.e-button--hover:not(.e-button--touch):not(.e-button--progress) {
        border-color: functions.get-theme-var('e-button-primary-border-color--state');
        background-color: functions.get-theme-var('e-button-primary-background-color--state');
        color: functions.get-theme-var('e-button-primary-text-color--state');
      }

      // :not(disabled) is required to increase weight.
      &.e-button:active:not([disabled]):not(.e-button--progress),
      &.e-button--active:not([disabled]):not(.e-button--progress) {
        border-color: functions.get-theme-var('e-button-primary-border-color--active');
        background-color: functions.get-theme-var('e-button-primary-background-color');
        color: functions.get-theme-var('e-button-primary-text-color--active');
      }

      &.e-button--progress {
        border-color: functions.get-theme-var('e-button-primary-background-color--disabled');
        background-color: functions.get-theme-var('e-button-primary-background-color--disabled');
      }
    }

    &[disabled],
    &.e-button--disabled {
      border-color: functions.get-theme-var(
        'e-button-primary-border-color--disabled'
      ) !important; // stylelint-disable-line declaration-no-important
      background-color: functions.get-theme-var(
        'e-button-primary-background-color--disabled'
      ) !important; // stylelint-disable-line declaration-no-important
      color: functions.get-theme-var(
        'e-button-primary-text-color--disabled'
      ) !important; // stylelint-disable-line declaration-no-important
    }
  }

  .e-button--variant-primary-quiet,
  .e-button--variant-primary-text {
    &:not([disabled]) {
      border-color: functions.get-theme-var('e-button-primary-quiet-border-color');
      background: functions.get-theme-var('e-button-primary-quiet-background-color');
      color: functions.get-theme-var('e-button-primary-quiet-text-color');

      &.e-button:focus,
      &.e-button--focus,
      &.e-button:hover:not(.e-button--touch),
      &.e-button--hover:not(.e-button--touch) {
        border-color: functions.get-theme-var('e-button-primary-quiet-border-color');
        color: functions.get-theme-var('e-button-primary-quiet-text-color--state');
      }

      // :not(disabled) is required to increase weight.
      &.e-button:active:not([disabled]),
      &.e-button--active:not([disabled]) {
        border-color: functions.get-theme-var('e-button-primary-quiet-border-color');
        color: functions.get-theme-var('e-button-primary-quiet-text-color--state');
      }
    }
  }

  .e-button--variant-secondary {
    &:not([disabled]) {
      border-color: functions.get-theme-var('e-button-secondary-border-color');
      background: functions.get-theme-var('e-button-secondary-background-color');
      color: functions.get-theme-var('e-button-secondary-text-color');

      &.e-button:focus:not(.e-button--progress),
      &.e-button--focus:not(.e-button--progress),
      &.e-button:hover:not(.e-button--touch):not(.e-button--progress),
      &.e-button--hover:not(.e-button--touch):not(.e-button--progress) {
        border-color: functions.get-theme-var('e-button-secondary-border-color--state');
        background-color: functions.get-theme-var('e-button-secondary-background-color--state');
        color: functions.get-theme-var('e-button-secondary-text-color--state');
      }

      // :not(disabled) is required to increase weight.
      &.e-button:active:not([disabled]):not(.e-button--progress),
      &.e-button--active:not([disabled]):not(.e-button--progress) {
        border-color: functions.get-theme-var('e-button-secondary-border-color--state');
        background-color: functions.get-theme-var('e-button-secondary-background-color--state');
        color: functions.get-theme-var('e-button-secondary-text-color--state');
      }
    }

    &.e-button--progress {
      background-color: functions.get-theme-var('e-button-background-color');
    }
  }

  .e-button--variant-secondary-quiet,
  .e-button--variant-secondary-text {
    &:not([disabled]) {
      border-color: functions.get-theme-var('e-button-secondary-quiet-border-color');
      background: functions.get-theme-var('e-button-secondary-quiet-background-color');
      color: functions.get-theme-var('e-button-secondary-quiet-text-color');

      &.e-button:focus,
      &.e-button--focus,
      &.e-button:hover:not(.e-button--touch),
      &.e-button--hover:not(.e-button--touch) {
        border-color: functions.get-theme-var('e-button-secondary-quiet-border-color');
        color: functions.get-theme-var('e-button-secondary-quiet-text-color--state');
      }

      // :not(disabled) is required to increase weight.
      &.e-button:active:not([disabled]),
      &.e-button--active:not([disabled]) {
        border-color: functions.get-theme-var('e-button-secondary-quiet-border-color');
        color: functions.get-theme-var('e-button-secondary-quiet-text-color--state');
      }
    }
  }

  .e-button--variant-primary-quiet,
  .e-button--variant-primary-text,
  .e-button--variant-secondary-quiet,
  .e-button--variant-secondary-text {
    &[disabled],
    &.e-button--disabled {
      border-color: functions.get-theme-var('e-button-primary-quiet-border-color--disabled');
      color: functions.get-theme-var(
        'e-button-primary-quiet-text-color--disabled'
      ) !important; // stylelint-disable-line declaration-no-important
    }
  }

  .e-button--variant-primary-text,
  .e-button--variant-secondary-text {
    > .e-button__inner {
      font-size: 0.8rem;
    }
  }
</style>
